import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { MapPin, Navigation, Home, ChevronRight, Maximize2 } from 'lucide-react';
import { Button } from './ui/button';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Create custom apiary marker icon with hive count
const createApiaryIcon = (hiveCount) => {
  return new L.DivIcon({
    className: 'custom-apiary-marker',
    html: `
      <div style="
        background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
        width: 44px;
        height: 44px;
        border-radius: 50%;
        border: 3px solid #d97706;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
        font-weight: bold;
        color: #78350f;
        box-shadow: 0 3px 8px rgba(0,0,0,0.3);
        position: relative;
      ">
        <span style="font-size: 18px;">🏠</span>
        <div style="
          position: absolute;
          bottom: -6px;
          right: -6px;
          background: #dc2626;
          color: white;
          width: 22px;
          height: 22px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          font-weight: bold;
          border: 2px solid white;
        ">${hiveCount}</div>
      </div>
    `,
    iconSize: [44, 44],
    iconAnchor: [22, 22],
    popupAnchor: [0, -22],
  });
};

// Component to fit map bounds to all markers
const FitBoundsToMarkers = ({ yards }) => {
  const map = useMap();
  
  useEffect(() => {
    if (!map) return;
    
    try {
      const yardsWithCoords = yards.filter(y => y.gps_lat && y.gps_lng);
      if (yardsWithCoords.length > 0) {
        const bounds = L.latLngBounds(
          yardsWithCoords.map(y => [y.gps_lat, y.gps_lng])
        );
        // Use setTimeout to ensure map is fully initialized
        const timeoutId = setTimeout(() => {
          try {
            if (map && map._container) {
              map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
            }
          } catch (e) {
            // Map might be unmounted, ignore error
            console.debug('Map fitBounds skipped - component may be unmounting');
          }
        }, 100);
        
        return () => clearTimeout(timeoutId);
      }
    } catch (e) {
      console.debug('FitBoundsToMarkers error:', e);
    }
  }, [yards, map]);
  
  return null;
};

const ApiaryMap = ({ yards = [], hives = [], onSelectYard }) => {
  const mapRef = useRef(null);
  const [mapCenter, setMapCenter] = useState([-33.8688, 151.2093]); // Default: Sydney
  const [mapZoom, setMapZoom] = useState(8); // Start more zoomed out
  const [isMapReady, setIsMapReady] = useState(false);

  // Handle yard selection with delay to prevent map errors
  const handleYardSelect = useCallback((yardName) => {
    if (onSelectYard) {
      // Small delay to let any pending map operations complete
      setTimeout(() => {
        onSelectYard(yardName);
      }, 50);
    }
  }, [onSelectYard]);

  // Calculate map bounds to fit all yards
  useEffect(() => {
    const yardsWithCoords = yards.filter(y => y.gps_lat && y.gps_lng);
    if (yardsWithCoords.length > 0) {
      const lats = yardsWithCoords.map(y => y.gps_lat);
      const lngs = yardsWithCoords.map(y => y.gps_lng);
      
      const avgLat = lats.reduce((sum, lat) => sum + lat, 0) / lats.length;
      const avgLng = lngs.reduce((sum, lng) => sum + lng, 0) / lngs.length;
      setMapCenter([avgLat, avgLng]);
      
      // Calculate zoom to fit all markers with padding
      if (yardsWithCoords.length > 1) {
        const latSpread = Math.max(...lats) - Math.min(...lats);
        const lngSpread = Math.max(...lngs) - Math.min(...lngs);
        const maxSpread = Math.max(latSpread, lngSpread);
        
        // More aggressive zoom out to show all markers
        if (maxSpread > 5) setMapZoom(5);
        else if (maxSpread > 3) setMapZoom(6);
        else if (maxSpread > 2) setMapZoom(7);
        else if (maxSpread > 1) setMapZoom(8);
        else if (maxSpread > 0.5) setMapZoom(9);
        else if (maxSpread > 0.2) setMapZoom(10);
        else if (maxSpread > 0.1) setMapZoom(11);
        else setMapZoom(12);
      } else {
        // Single yard - zoom in more
        setMapZoom(13);
      }
    }
  }, [yards]);

  // Get hives for a specific yard
  const getYardHives = (yardName) => {
    return hives.filter(h => h.yard === yardName);
  };

  // Generate Google Maps directions URL
  const getDirectionsUrl = (yard) => {
    if (yard.gps_lat && yard.gps_lng) {
      return `https://www.google.com/maps/dir/?api=1&destination=${yard.gps_lat},${yard.gps_lng}`;
    } else if (yard.location) {
      return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(yard.location)}`;
    }
    return null;
  };

  // Fit map to show all markers
  const fitAllMarkers = useCallback(() => {
    if (mapRef.current) {
      try {
        const yardsWithCoords = yards.filter(y => y.gps_lat && y.gps_lng);
        if (yardsWithCoords.length > 0 && mapRef.current._container) {
          const bounds = L.latLngBounds(
            yardsWithCoords.map(y => [y.gps_lat, y.gps_lng])
          );
          mapRef.current.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
        }
      } catch (e) {
        console.debug('fitAllMarkers error:', e);
      }
    }
  }, [yards]);

  // Filter yards that have GPS coordinates
  const mappableYards = yards.filter(y => y.gps_lat && y.gps_lng);

  if (mappableYards.length === 0) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
        <MapPin className="h-12 w-12 text-amber-400 mx-auto mb-3" />
        <h3 className="font-medium text-amber-800 mb-2">No Mapped Apiaries</h3>
        <p className="text-sm text-amber-600">
          Add GPS coordinates to your apiaries to see them on the map.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden" data-testid="apiary-map">
      <div className="p-3 sm:p-4 border-b border-stone-100 bg-gradient-to-r from-amber-50 to-orange-50 flex items-center justify-between gap-2">
        <h2 className="font-bold text-stone-800 text-base sm:text-lg flex items-center gap-2">
          <MapPin className="h-4 sm:h-5 w-4 sm:w-5 text-amber-600" />
          <span className="hidden sm:inline">Apiary Locations</span>
          <span className="sm:hidden">Map</span>
          <span className="text-xs sm:text-sm font-normal text-stone-500">({mappableYards.length})</span>
        </h2>
        <Button
          variant="outline"
          size="sm"
          onClick={fitAllMarkers}
          className="flex items-center gap-1 text-xs px-2 sm:px-3"
        >
          <Maximize2 className="h-3 w-3" />
          <span className="hidden sm:inline">Show All</span>
        </Button>
      </div>
      
      <div className="h-[250px] sm:h-[400px] relative" style={{ zIndex: 0 }}>
        <MapContainer
          ref={mapRef}
          center={mapCenter}
          zoom={mapZoom}
          style={{ height: '100%', width: '100%', zIndex: 0 }}
          scrollWheelZoom={true}
          whenReady={() => setIsMapReady(true)}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          {/* Auto-fit bounds on load */}
          <FitBoundsToMarkers yards={mappableYards} />
          {mappableYards.map(yard => {
            const yardHives = getYardHives(yard.name);
            const directionsUrl = getDirectionsUrl(yard);
            
            return (
              <Marker
                key={yard.id}
                position={[yard.gps_lat, yard.gps_lng]}
                icon={createApiaryIcon(yardHives.length)}
              >
                <Popup minWidth={250} maxWidth={320}>
                  <div className="p-1">
                    {/* Header */}
                    <div className="flex items-center gap-2 mb-3 pb-2 border-b border-stone-200">
                      <div className="bg-amber-100 p-2 rounded-lg">
                        <Home className="h-5 w-5 text-amber-700" />
                      </div>
                      <div>
                        <h3 className="font-bold text-stone-900 text-lg">{yard.name}</h3>
                        <p className="text-xs text-stone-500">{yard.location || 'No address'}</p>
                      </div>
                    </div>
                    
                    {/* Hive Count */}
                    <div className="bg-amber-50 rounded-lg p-3 mb-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-amber-800">
                          {yardHives.length} Hive{yardHives.length !== 1 ? 's' : ''}
                        </span>
                        {onSelectYard && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 text-xs text-amber-700 hover:bg-amber-100"
                            onClick={() => handleYardSelect(yard.name)}
                          >
                            View <ChevronRight className="h-3 w-3 ml-1" />
                          </Button>
                        )}
                      </div>
                      
                      {/* Hive Numbers List */}
                      {yardHives.length > 0 ? (
                        <div className="flex flex-wrap gap-1.5">
                          {yardHives
                            .sort((a, b) => (a.hive_number || 0) - (b.hive_number || 0))
                            .map(hive => (
                              <span
                                key={hive.id}
                                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-white border border-amber-300 text-amber-800"
                              >
                                #{hive.hive_number || hive.name || '?'}
                              </span>
                            ))}
                        </div>
                      ) : (
                        <p className="text-xs text-amber-600 italic">No hives at this location</p>
                      )}
                    </div>
                    
                    {/* Owner Info */}
                    {yard.owner_name && (
                      <div className="text-xs text-stone-600 mb-3">
                        <span className="font-medium">Owner:</span> {yard.owner_name} {yard.owner_surname || ''}
                        {yard.owner_mobile && (
                          <a 
                            href={`tel:${yard.owner_mobile}`}
                            className="ml-2 inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 font-medium"
                          >
                            📞 {yard.owner_mobile}
                          </a>
                        )}
                      </div>
                    )}
                    
                    {/* Directions Button */}
                    {directionsUrl && (
                      <a
                        href={directionsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 w-full bg-blue-500 hover:bg-blue-600 text-black font-semibold py-2 px-4 rounded-lg text-sm transition-colors border border-blue-600"
                      >
                        <Navigation className="h-4 w-4" />
                        Get Directions
                      </a>
                    )}
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>
      </div>
      
      {/* Legend */}
      <div className="p-3 bg-stone-50 border-t border-stone-100 flex items-center justify-center gap-4 text-xs text-stone-600">
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 bg-amber-400 rounded-full border-2 border-amber-600"></div>
          <span>Apiary Location ({mappableYards.length})</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 bg-red-500 rounded-full border-2 border-white text-white text-[8px] flex items-center justify-center font-bold">{hives.length}</div>
          <span>Hive Count</span>
        </div>
      </div>
    </div>
  );
};

export default ApiaryMap;
